import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import SeoHead from "../components/seo/SeoHead";
import Footer from "../components/Footer";

const THEME_KEY = "hookora_theme";

function PrivacyPolicyPage() {
  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      pageBg: isDark ? "#020617" : "#f8fafc",
      pageGradient: isDark
        ? "radial-gradient(circle at top left, rgba(59,130,246,0.12), transparent 28%), radial-gradient(circle at top right, rgba(124,58,237,0.10), transparent 24%)"
        : "none",
      cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      border: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#4b5563",
      badgeBg: isDark ? "rgba(8,47,73,0.28)" : "#ecfeff",
      badgeText: isDark ? "#a5f3fc" : "#155e75",
      shadow: isDark
        ? "0 14px 40px rgba(2, 6, 23, 0.45)"
        : "0 10px 30px rgba(15, 23, 42, 0.05)"
    }),
    [isDark]
  );

  const sectionTitleStyle = {
    color: colors.text,
    marginBottom: "10px"
  };

  const paragraphStyle = {
    color: colors.textSoft,
    lineHeight: "1.8"
  };

  const listStyle = {
    color: colors.textSoft,
    lineHeight: "1.9",
    paddingLeft: "20px"
  };

  return (
    <div style={{ background: colors.pageBg, backgroundImage: colors.pageGradient, minHeight: "100vh" }}>
      <SeoHead
        title="Privacy Policy | Hookora"
        description="Read Hookora's privacy policy covering account information, billing data, support messages, and platform usage."
        path="/privacy-policy"
      />

      <Navbar />

      <div
        style={{
          maxWidth: "900px",
          margin: "0 auto",
          padding: "32px 16px 56px"
        }}
      >
        <div
          style={{
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: "24px",
            padding: "28px",
            boxShadow: colors.shadow,
            backdropFilter: isDark ? "blur(14px)" : "none"
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 12px",
              borderRadius: "999px",
              background: colors.badgeBg,
              color: colors.badgeText,
              fontSize: "12px",
              fontWeight: "700",
              marginBottom: "14px",
              border: isDark ? `1px solid ${colors.border}` : "none"
            }}
          >
            Privacy
          </div>

          <h1
            style={{
              marginTop: 0,
              marginBottom: "10px",
              color: colors.text,
              fontSize: "34px",
              lineHeight: "1.1"
            }}
          >
            Privacy Policy
          </h1>

          <p
            style={{
              color: colors.textMuted,
              lineHeight: "1.7",
              marginBottom: "28px",
              fontSize: "15px"
            }}
          >
            This Privacy Policy explains how Hookora collects, uses, and protects
            information when you use our website, platform, and related services.
          </p>

          <section style={{ marginBottom: "28px" }}>
            <h2 style={sectionTitleStyle}>
              Information we collect
            </h2>
            <p style={{ ...paragraphStyle, marginTop: 0 }}>
              When you use Hookora, we may collect:
            </p>
            <ul style={listStyle}>
              <li>account information such as your username, email address, and password</li>
              <li>billing-related information connected to subscriptions and payment status</li>
              <li>content inputs and generated outputs created through Hookora tools</li>
              <li>support messages, feedback, suggestions, or complaints you send to us</li>
              <li>usage information such as tool activity, feature usage, and account interactions</li>
            </ul>
          </section>

          <section style={{ marginBottom: "28px" }}>
            <h2 style={sectionTitleStyle}>
              How we use your information
            </h2>
            <p style={{ ...paragraphStyle, margin: 0 }}>
              We use information collected through Hookora to operate the platform,
              provide access to features, process subscriptions, improve product
              performance, respond to support requests, and communicate important
              service-related updates.
            </p>
          </section>

          <section style={{ marginBottom: "28px" }}>
            <h2 style={sectionTitleStyle}>
              Billing and payments
            </h2>
            <p style={{ ...paragraphStyle, margin: 0 }}>
              Hookora uses third-party payment providers to process paid subscriptions.
              We do not store your full payment card details on our platform.
              Billing-related information we receive may include subscription status,
              customer codes, plan details, and payment references.
            </p>
          </section>

          <section style={{ marginBottom: "28px" }}>
            <h2 style={sectionTitleStyle}>
              Support and communication
            </h2>
            <p style={{ ...paragraphStyle, margin: 0 }}>
              If you contact Hookora through email or the support form, we may keep
              your message and related contact details in order to respond, provide
              assistance, and improve the service.
            </p>
          </section>

          <section style={{ marginBottom: "28px" }}>
            <h2 style={sectionTitleStyle}>
              Data protection
            </h2>
            <p style={{ ...paragraphStyle, margin: 0 }}>
              Hookora takes reasonable steps to protect user information. However,
              no online system can guarantee absolute security, and users should also
              take care to protect their login credentials.
            </p>
          </section>

          <section style={{ marginBottom: "28px" }}>
            <h2 style={sectionTitleStyle}>
              Your choices
            </h2>
            <p style={{ ...paragraphStyle, margin: 0 }}>
              You may contact Hookora if you need help with your account, support
              issues, or subscription-related concerns. You may also stop using the
              service at any time.
            </p>
          </section>

          <section>
            <h2 style={sectionTitleStyle}>
              Changes to this policy
            </h2>
            <p style={{ ...paragraphStyle, margin: 0 }}>
              Hookora may update this Privacy Policy from time to time. Continued use
              of the platform after updates means you accept the revised policy.
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default PrivacyPolicyPage;